import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EmptyState, Icon, PageHeader, Tone, WaitingInline } from '~/components/ui';
import type { ThreadDetail, ThreadMessage } from './api/threadsClient';
import { ThreadApiError, fetchThread, replyToThread } from './api/threadsClient';
import {
  THREAD_STATUS_TONES,
  absoluteTime,
  gmailThreadUrl,
  notifyInboxChanged,
  threadStatusLabel,
} from './lib/threads';
import { useLocale, useT } from '~/i18n';
import { API_BASE } from '~/lib/api';
import MessageBubble from './components/MessageBubble';
import ReplyComposer from './components/ReplyComposer';
import SuggestedReplyCard from './components/SuggestedReplyCard';
import { messageToSuggestFor } from './lib/replySuggestion';

/**
 * El detalle de un hilo: la postulación y todo lo que vino después.
 *
 * El primer mensaje NO existe como fila —se sintetiza al leer desde la propia
 * application— y por eso llega con `synthesized: true` y un `_id` derivado
 * (`<appId>:application`). Se pinta como cualquier otra burbuja, pero rotulado
 * "Postulación enviada": ninguna acción del hilo puede accionarlo por id.
 *
 * Los dos canales se pintan IGUAL: en gmail el servidor lee el hilo de la
 * casilla del candidato y lo devuelve con la misma forma que domain, y la
 * respuesta sale desde su Gmail (lo dice una línea bajo el compositor). La
 * pantalla sólo mira `can_reply` y `gmail_error`, sin provocar el 409:
 *   - can_reply → compositor (y sugerencia si la última palabra es de la empresa);
 *   - gmail_reconnect_required → "Reconecta tu Gmail" con el flujo de conexión
 *     de siempre (/job-apply/auth/start);
 *   - gmail_unavailable → reintentar, o abrirlo en Gmail;
 *   - lo demás (hilo viejo sin ids, borrado de la casilla) → abrirlo en Gmail.
 */
export default function JobApplyThreadPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const t = useT('workspace').thread;
  const locale = useLocale();
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  // Lo que la respuesta sugerida le pasa al compositor. El nonce hace que
  // elegir otra vez el mismo borrador lo vuelva a poner.
  //
  // Lleva el hilo al que pertenece: react-router NO remonta esta página al
  // pasar de /mensajes/a1 a /mensajes/a2, así que un borrador escrito para una
  // empresa sobreviviría al cambio y aparecería en el compositor de OTRA. Sólo
  // se le pasa al compositor si es de este hilo.
  const [prefill, setPrefill] = useState<{ text: string; nonce: number; applicationId: string } | null>(null);

  // Relee el hilo SIN pasar por el estado de carga: el compositor (y lo que la
  // persona escribió) se queda montado. Lo usa el 409 `recipient_changed`.
  const refresh = useCallback(async () => {
    if (!applicationId) return;
    try {
      const data = await fetchThread(applicationId);
      setThread(data.thread);
      setMessages(data.messages);
    } catch {
      // Si falla, queda lo que había; el error del envío ya está a la vista.
    }
  }, [applicationId]);

  const load = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError(false);
    try {
      const data = await fetchThread(applicationId);
      setThread(data.thread);
      setMessages(data.messages);
      // Abrir el hilo dejó mensajes leídos: el badge del nav ya no vale. El nav
      // es de otro dueño, así que se le avisa por evento y él decide.
      if (data.thread.marked_read > 0) notifyInboxChanged();
    } catch (err) {
      if (err instanceof ThreadApiError && err.status === 404) setNotFound(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSend = async (text: string, replyId: string) => {
    if (!applicationId || !thread) return;
    setPendingText(text);
    try {
      // El destinatario que el compositor mostró: el servidor no manda a otro.
      const message = await replyToThread(applicationId, text, {
        to: thread.reply_to || thread.contact_email,
        clientReplyId: replyId,
      });
      setMessages((prev) => [...prev, message]);
    } catch (err) {
      // La empresa contestó desde otra dirección (o se ignoró un Reply-To): se
      // relee el hilo para mostrar el destinatario nuevo, y el compositor dice
      // por qué no salió. El texto sigue escrito.
      if (err instanceof ThreadApiError && err.code === 'recipient_changed') void refresh();
      throw err;
    } finally {
      // Salga bien o mal, la burbuja provisional se va: si falló, el texto sigue
      // vivo en el compositor, que es de donde se reintenta.
      setPendingText(null);
    }
  };

  if (loading) return <WaitingInline label={t.loading} />;

  if (notFound || !thread) {
    return (
      <div className="space-y-6">
        <PageHeader scope={t.scope} title={t.fallbackTitle} />
        <EmptyState
          icon="search"
          title={t.notFoundTitle}
          note={t.notFoundNote}
          action={
            <Link to="/mensajes" className="btn btn-primary btn-sm">
              {t.seeMessages}
            </Link>
          }
        />
      </div>
    );
  }

  // Sólo cuando la última palabra es de la empresa y se puede responder desde
  // acá. Al mandar la respuesta el último mensaje pasa a ser propio y la
  // tarjeta se va sola.
  const toSuggestFor = messageToSuggestFor(thread, messages);
  const applySuggestion = (text: string) =>
    setPrefill((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1, applicationId: applicationId ?? '' }));
  const prefillHere = prefill && prefill.applicationId === applicationId ? prefill : null;

  const pendingMessage: ThreadMessage | null = pendingText
    ? {
        _id: 'pending',
        synthesized: false,
        direction: 'outbound',
        from_address: '',
        to_address: thread.contact_email,
        subject: '',
        body_text: pendingText,
        body_html: null,
        created_at: new Date().toISOString(),
        read_at: null,
      }
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        scope={t.scope}
        title={thread.title}
        lede={
          <>
            {thread.company}
            {thread.location && <> · {thread.location}</>}
          </>
        }
        actions={
          <>
            {thread.reply_is_opportunity && <Tone tone="good">{t.opportunity}</Tone>}
            <Tone tone={THREAD_STATUS_TONES[thread.status] ?? 'muted'}>
              {threadStatusLabel(thread.status, locale)}
            </Tone>
            <Link to="/mensajes" className="btn btn-ghost btn-sm">
              {t.back}
            </Link>
          </>
        }
      />

      {error && <div className="alert alert-error text-sm">{t.loadError}</div>}

      {thread.sent_at && (
        <p className="text-xs hush">
          {t.appliedOn(absoluteTime(thread.sent_at, locale))}
          {thread.contact_email && <>{t.wroteTo(thread.contact_email)}</>}
        </p>
      )}

      <ul className="thread">
        {messages.map((message) => (
          <MessageBubble key={String(message._id)} message={message} />
        ))}
        {pendingMessage && <MessageBubble key="pending" message={pendingMessage} pending />}
      </ul>

      {toSuggestFor && !pendingText && applicationId && (
        // La key es hilo + mensaje que se contesta: si cambia cualquiera, la tarjeta se
        // remonta limpia y pide la sugerencia nueva.
        <SuggestedReplyCard
          key={`${applicationId}:${String(toSuggestFor._id)}`}
          applicationId={applicationId}
          onUse={applySuggestion}
        />
      )}

      {thread.can_reply ? (
        <div className="card">
          <div className="card-body">
            <span className="brand-label mb-2 block">{t.reply}</span>
            {/* key por hilo: lo escrito a medias para una empresa no viaja a otra. */}
            <ReplyComposer
              key={applicationId}
              onSend={handleSend}
              to={thread.reply_to || thread.contact_email}
              prefill={prefillHere}
            />
            {thread.send_channel === 'gmail' && (
              <p className="text-xs hush mt-2">{t.gmailReplyNote(thread.gmail_address ?? '')}</p>
            )}
          </div>
        </div>
      ) : thread.gmail_error === 'gmail_reconnect_required' ? (
        /* El permiso de Google se venció: el mismo flujo de conexión de siempre. */
        <div className="card">
          <div className="card-body">
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5">
                <Icon name="mail" size={20} />
              </span>
              <div className="space-y-2">
                <p className="font-medium">{t.gmailReconnectTitle}</p>
                <p className="text-sm hush">{t.gmailReconnectNote}</p>
                <div className="flex flex-wrap gap-2">
                  <a className="btn btn-primary btn-sm gap-2 w-fit" href={`${API_BASE}/job-apply/auth/start`}>
                    <Icon name="refresh" />
                    {t.reconnectGmail}
                  </a>
                  <a className="btn btn-ghost btn-sm gap-2 w-fit" href={gmailThreadUrl(thread)} target="_blank" rel="noreferrer">
                    <Icon name="arrow-right" />
                    {t.openInGmail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : thread.gmail_error === 'gmail_unavailable' ? (
        <div className="card">
          <div className="card-body">
            <div className="space-y-2">
              <p className="font-medium">{t.gmailUnavailableTitle}</p>
              <p className="text-sm hush">{t.gmailUnavailableNote}</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn btn-primary btn-sm gap-2 w-fit" onClick={() => void load()}>
                  <Icon name="refresh" />
                  {t.retry}
                </button>
                <a className="btn btn-ghost btn-sm gap-2 w-fit" href={gmailThreadUrl(thread)} target="_blank" rel="noreferrer">
                  <Icon name="arrow-right" />
                  {t.openInGmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Hilo de Gmail que no se puede leer desde acá (sin ids, o borrado de la casilla). */
        <div className="card">
          <div className="card-body">
            <div className="flex items-start gap-3">
              <span className="text-brand mt-0.5">
                <Icon name="mail" size={20} />
              </span>
              <div className="space-y-2">
                <p className="font-medium">{t.gmailTitle}</p>
                <p className="text-sm hush">{t.gmailNote}</p>
                <a
                  className="btn btn-primary btn-sm gap-2 w-fit"
                  href={gmailThreadUrl(thread)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="arrow-right" />
                  {t.openInGmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
